import { useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Mail, Wheat, ShieldCheck } from "lucide-react";

export default function GranosLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0f5233] text-white overflow-hidden relative">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-lime-400/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl" />

      <div className="flex-1 flex flex-col justify-center px-6 py-10 relative z-10">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="w-full max-w-sm mx-auto">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="h-20 w-20 rounded-3xl bg-white/10 border border-white/20 flex items-center justify-center shadow-lg mb-4">
              <Wheat className="h-10 w-10 text-lime-300" />
            </div>
            <div className="bg-white rounded-xl px-3 py-1.5 shadow-sm">
              <img src="/logo-tecnomyl.png" alt="Tecnomyl S.A." className="h-8 w-auto object-contain" />
            </div>
            <h1 className="mt-5 font-heading text-3xl font-extrabold tracking-tight">Tecnomyl Granos</h1>
            <p className="mt-1.5 text-sm text-emerald-100/80">Contratos, informes y cierres de precio</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="g-email" className="text-emerald-100">Correo corporativo</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-200/70" />
                <Input id="g-email" type="email" data-testid="granos-login-email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="usuario@tecnomyl.com" required
                  className="pl-9 h-12 bg-white/10 border-white/20 text-white placeholder:text-emerald-200/50 focus-visible:ring-lime-400" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-pass" className="text-emerald-100">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-200/70" />
                <Input id="g-pass" type="password" data-testid="granos-login-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                  className="pl-9 h-12 bg-white/10 border-white/20 text-white placeholder:text-emerald-200/50 focus-visible:ring-lime-400" />
              </div>
            </div>

            {error && (
              <div data-testid="granos-login-error" className="text-sm text-red-100 bg-red-500/20 border border-red-300/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button type="submit" data-testid="granos-login-submit" disabled={loading}
              className="w-full h-12 bg-lime-400 hover:bg-lime-300 text-[#0f5233] font-bold text-base">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ingresar
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-emerald-200/70">
            <ShieldCheck className="h-4 w-4" />
            Acceso restringido al equipo de Granos
          </div>
        </motion.div>
      </div>
      <div className="pb-6 text-center text-[11px] text-emerald-200/60 relative z-10">
        © {new Date().getFullYear()} Tecnomyl S.A.
      </div>
    </div>
  );
}
